<template>
  <div
    ref="$el"
    class="call-to-action"
    :class="theme"
  >
    <div class="gradient" />
    <UiComponentImage
      alt="Camera"
      class="camera"
      :expected-size="cameraSize"
      :height="1677"
      source="https://livestorm.imgix.net/1127/1664802949-camera.png"
      :width="1677"
    />
    <div class="pattern">
      <UiComponentImage
        alt="pattern"
        class="mobile"
        :expected-size="imageWidth"
        :height="imageHeight"
        orientation="portrait"
        :source="
          imageMobile && imageMobile.url
            ? imageMobile.url
            : 'https://livestorm.imgix.net/1127/1621603238-call-to-action-pattern.png'
        "
        :width="imageWidth"
      />
      <UiComponentImage
        alt="pattern"
        class="desktop"
        :expected-size="imageWidth"
        :extra-params="{
          ...((imageDesktop && imageDesktop.extraParams) || {}),
          h: imageHeight,
          w: imageWidth,
        }"
        :height="imageHeight"
        :source="
          imageDesktop && imageDesktop.url
            ? imageDesktop.url
            : 'https://livestorm.imgix.net/1127/1621603238-call-to-action-pattern.png'
        "
        :width="imageWidth"
      />
    </div>
    <div class="content">
      <UiComponentHeading
        :key="`heading-${headingLevel}`"
        as="paragraph"
        :level="headingLevel"
      >
        <slot name="heading" />
      </UiComponentHeading>
      <UiComponentParagraph><slot name="paragraph" /></UiComponentParagraph>
      <div class="buttons">
        <AtomButton
          :action="(primaryButton.event && primaryButton.event.name) || primaryButton.action"
          :attributes="(primaryButton.event && primaryButton.event.attributes) || primaryButton.attributes"
          :class="primaryButton.class"
          :label="primaryButton.label"
          size="l"
          :to="primaryButton.to"
          @click="emit('primary:click', $event)"
        >
          {{ primaryButton.slot }}
        </AtomButton>
        <AtomButton
          v-if="secondaryButton"
          :action="(secondaryButton.event && secondaryButton.event.name) || secondaryButton.action"
          :attributes="(secondaryButton.event && secondaryButton.event.attributes) || secondaryButton.attributes"
          :label="secondaryButton.label"
          size="l"
          :to="secondaryButton.to"
          :type="secondaryButton.type || 'secondary'"
          @click="emit('secondary:click', $event)"
        >
          {{ secondaryButton.slot }}
        </AtomButton>
      </div>
      <ul
        v-if="features.length > 0"
        class="items"
      >
        <AtomListItem
          v-for="(feature, index) in features"
          :key="index"
        >
          {{ feature }}
        </AtomListItem>
      </ul>
    </div>
  </div>
</template>

<script setup>
// Composables
const { width: screenWidth } = useScreenSize()

// Emitter
const emit = defineEmits([
  'primary:click',
  'secondary:click',
])

// Props
const props = defineProps({
  features: {
    default() {
      return []
    },
    type: Array,
  },
  imageDesktop: {
    default() {
      return undefined
    },
    required: false,
    type: Object,
  },
  imageMobile: {
    default() {
      return undefined
    },
    required: false,
    type: Object,
  },
  primaryButton: {
    required: true,
    type: Object,
  },
  secondaryButton: {
    default() {
      return undefined
    },
    required: false,
    type: Object,
  },
  theme: {
    default() {
      return 'livestorm'
    },
    required: false,
    type: String,
    validator(value) {
      return ['dark', 'enterprise', 'livestorm', 'new'].indexOf(value) !== -1
    },
  },
})

// Refs
const $el = ref(null)
const imageHeight = ref(400)
const imageWidth = ref(1328)
const resizeObserver = ref(null)

// Computed Properties
const cameraSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return 199
    case screenWidth.valu >= 768 && screenWidth.valu < 1024:
      return 352
    default:
      return 559
  }
})

const headingLevel = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return props.theme === 'new' ? 3 : 2
    default:
      return 2
  }
})

// Methods
function onObserve() {
  imageHeight.value = $el.value.offsetHeight
  imageWidth.value = $el.value.offsetWidth
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  resizeObserver.value.unobserve($el.value)
})

onMounted(() => {
  resizeObserver.value = new ResizeObserver(onObserve)
  resizeObserver.value.observe($el.value)
})
</script>

<style lang="postcss" scoped>
.call-to-action {
  @apply relative flex flex-col w-full p-8 bg-livestorm-blue-700 rounded-4xl overflow-hidden;

  @screen md {
    @apply justify-center items-start p-16;
  }

  .pattern {
    .desktop {
      @apply hidden;

      @screen md {
        @apply flex;
      }
    }

    .mobile {
      @apply flex;

      @screen md {
        @apply hidden;
      }
    }
  }

  &.enterprise {
    @apply bg-transparent;

    .content {
      .h2 {
        @apply text-grey-blue-900 !important;
      }

      p {
        @apply text-grey-blue-700;
      }

      .buttons {
        .button {
          &.primary {
            @apply bg-livestorm-blue-700;

            &:hover {
              @apply bg-livestorm-blue-800;
            }
          }

          &.secondary {
            @apply bg-white text-grey-blue-900;

            &:hover {
              @apply bg-grey-blue-100;
            }
          }
        }
      }

      .items {
        li {
          @apply text-grey-blue-700;

          &:before {
            @apply text-grey-blue-700 !important;
          }
        }
      }
    }
  }

  &.dark {
    @apply bg-grey-blue-900;

    .content {
      .buttons {
        .button {
          &.primary {
            @apply bg-livestorm-blue-700;

            &:hover {
              @apply bg-livestorm-blue-800;
            }
          }
        }
      }
    }
  }

  &.new {
    @apply bg-livestorm-blue-100 p-8 border-2 border-livestorm-blue-200 overflow-visible;

    @screen md {
      @apply p-16;
    }

    .camera {
      @apply absolute flex rounded-none transition-none z-0;
      bottom: -35px;
      right: -52px;

      @screen md {
        bottom: -51px;
        right: -91px;
      }

      @screen xl {
        @apply transform-gpu translate-y-1/2;
        bottom: 50%;
        right: -144px;
      }
    }

    .gradient {
      @screen md {
        @apply absolute inset-0 flex rounded-4xl z-0 overflow-hidden;
      }

      &::before {
        @apply absolute flex;
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(11, 66, 195, 0.15) 0%,
          rgba(11, 66, 195, 0) 100%
        );
        content: "";
        height: 906px;
        width: 906px;
        left: 477px;
        top: -229.46px;
      }
    }

    .pattern {
      @apply hidden;
    }

    .content {
      @screen md {
        max-width: 493px;
      }

      @screen xl {
        max-width: 612px;
      }

      .h2,
      .h3 {
        @apply mt-0 text-winter-green-900 !important;
      }

      .h3 + p,
      .h2 + p {
        @apply text-grey-blue-700 whitespace-normal;
      }

      .buttons {
        .button {
          @apply w-full shadow-none;

          @screen md {
            @apply w-auto;
          }

          &.primary {
            @apply bg-livestorm-blue-700;

            &:hover {
              @apply bg-livestorm-blue-800;
            }
          }
        }
      }

      .items {
        @apply items-start;
        max-width: 186px;

        @screen md {
          @apply max-w-none;
        }

        li {
          @apply text-grey-blue-600 text-sm leading-6;

          &::before {
            @apply text-grey-blue-600 !important;
          }
        }
      }
    }
  }

  .camera {
    @apply hidden;
  }

  .gradient {
    @apply hidden;
  }

  .pattern {
    @apply absolute right-0 bottom-0 flex justify-end items-end w-full h-full z-0;

    picture {
      @apply rounded-none;
    }
  }

  .content {
    @apply z-10;

    .h2 {
      @apply font-normal text-white text-4xl leading-14 !important;

      @screen md {
        @apply max-w-xl;
      }

      @screen mdminus {
        @apply text-5xl leading-16 !important;
      }

      @screen mdplus {
        @apply text-5xl leading-16 !important;
      }

      @screen xl {
        @apply max-w-4xl;
      }
    }

    p {
      @apply mt-4 text-livestorm-blue-200;

      @screen lg {
        @apply max-w-3xl;
      }
    }

    .buttons {
      @apply flex flex-col w-full mt-8;

      @screen md {
        @apply flex-row justify-start;
      }

      .button {
        @apply w-fit shadow-light4;

        &.primary {
          @apply bg-grey-blue-900;

          &:hover {
            @apply bg-black text-white;
          }
        }

        &.secondary {
          @apply mt-4 bg-grey-blue-200 text-winter-green-900;

          &:hover {
            @apply bg-grey-blue-300;
          }

          @screen md {
            @apply mt-0 ml-4;
          }
        }

        &.text {
          @apply mt-4 text-white shadow-none;

          @screen md {
            @apply mt-0;
          }
        }
      }
    }

    .items {
      @apply flex flex-col w-full mt-8;

      @screen md {
        @apply flex-row justify-start;
      }

      li {
        @apply text-white font-medium;

        &:last-child {
          @apply mt-4;

          @screen md {
            @apply mt-0 ml-4;
          }
        }

        &:before {
          @apply bg-transparent text-white font-semibold !important;
          font-size: 1.125rem;
        }
      }
    }
  }
}
</style>
